import { useState } from 'react';
import { api, type OverrideRow } from '../api';
import { label, money, ts, useLoad } from '../lib';

// Orders placed outside a vendor's guideline — below its minimum, or for a date it does not deliver.
// Commerce records each one and lets the order through: this is the list, read-only on purpose (making
// it an approval step would rebuild the block Intellam removed on 2026-08-06). A below-minimum PO is
// where a vendor may add a small-order fee, so AP reads it too. "What happened" is commerce's own
// sentence (the minimum and the shortfall, or the days the vendor delivers).
const WINDOWS = [7, 30, 90, 365];

export function OverridesPanel() {
  const [days, setDays] = useState(30);
  const { data, err } = useLoad(() => api.commerceOverrides(days), [days]);
  const byRule = (data ?? []).reduce<Record<string, number>>((m, r) => ({ ...m, [r.rule]: (m[r.rule] ?? 0) + 1 }), {});

  return (
    <div className="stack">
      <h1>Rule Overrides</h1>
      <p className="sub">Orders placed outside a vendor guideline (minimum order, delivery days). They went through; this is the record.</p>
      <div className="toolbar">
        {WINDOWS.map((d) => (
          <button key={d} className={'chip' + (days === d ? ' on' : '')} onClick={() => setDays(d)}>Last {d} days</button>
        ))}
      </div>
      {err && <p className="msg bad">⚠ {err}</p>}
      {data && data.length > 0 && (
        <div className="cards">
          <div className="stat"><div className="stat-k">Overrides</div><div className="stat-v">{data.length}</div><div className="stat-sub">last {days} days{data.length === 500 ? ' (newest 500)' : ''}</div></div>
          {Object.entries(byRule).map(([rule, n]) => (
            <div key={rule} className="stat"><div className="stat-k">{label(rule)}</div><div className="stat-v">{n}</div></div>
          ))}
        </div>
      )}
      <section className="card">
        <table>
          <thead><tr><th>When</th><th>PO #</th><th>Camp</th><th>Vendor</th><th>Rule</th><th>What happened</th><th className="num">Order</th><th>Status</th></tr></thead>
          <tbody>
            {(data ?? []).map((r: OverrideRow) => (
              <tr key={r.id}>
                <td className="mono muted">{ts(r.created_at)}</td>
                <td className="mono">{r.increment_id}</td>
                <td>{r.camp ?? '—'}</td>
                <td>{r.vendor ?? '—'}</td>
                <td><span className="pill type">{label(r.rule)}</span></td>
                <td className="small">
                  {r.detail ?? label(r.reason)}
                  {r.acknowledged && <div className="muted">buyer acknowledged{r.acknowledged_by ? ` (${r.acknowledged_by})` : ''}</div>}
                </td>
                <td className="num">{money(r.order_amount)}</td>
                <td><span className="pill type">{label(r.status)}</span></td>
              </tr>
            ))}
            {data && data.length === 0 && <tr><td colSpan={8} className="loading">No order was placed outside a vendor guideline in the last {days} days.</td></tr>}
          </tbody>
        </table>
        {!data && !err && <p className="loading">Loading…</p>}
      </section>
    </div>
  );
}
